import React, { FunctionComponent } from "react";
import styled from "styled-components";
import Image from "next/image";
import ReactCardFlip from "react-card-flip";

import * as T from "./Typography";
import * as u from "../utils";
import * as U from "./Utils";
import * as I from "./Icons";
import GOALS from "../types/user/goals";
import { UserModel, UserSearchModel } from "../lib/Domain/User/User";
import { toBusinessCard } from "../handlers/conversionHandler";
import MemberProfileModal from "./Profile/MemberProfileModal";
import RxQrCode from "./ui/display/RxQrCode";
import { Hit } from "instantsearch.js";
import config from "../config.client";
import { interests } from "../types/user/interests";

type StyleProps = {
  colour?: string;
  isOpaque?: boolean;
  isBold?: boolean;
  isGraphLayer?: boolean;
  href?: string;
  isStatic?: boolean;
};

type BusinessCardProps = {
  member: UserModel | Hit<UserSearchModel>;
  isStatic?: boolean;
};

const whiteTextColours = [
  "#525858",
  "#834973",
  "#6D8868",
  "#955C5C",
  "#617595",
  "#886BB7",
  "#50968D",
];

const BusinessCard: FunctionComponent<BusinessCardProps> = (props) => {
  if (!props) return null;

  const card = toBusinessCard(props.member);

  const stripTags = <T extends string>(item: T): T =>
    item && (item.replace(/<[^>]+>/g, "") as T);
  const sanitise = (item: string) =>
    item && item.replace(/<(?!\/?mark\b)[^>]*>/gi, "");

  const job = card.job || card.jobLevel;
  const jobCompany = card.jobCompany || card.jobIndustry;
  const role = [job, jobCompany].filter(Boolean).join(" • ");
  const userInterests = (card.interests || [])
    .filter((item) => interests.includes(stripTags(item)))
    .join(" • ");
  const goals = (card.goals || [])
    .filter((item) => GOALS.includes(stripTags(item)))
    .join(" • ");
  const textColour = whiteTextColours.includes(
    stripTags(card.businessCardColour)
  )
    ? u.colours.shell
    : u.colours.charcoal;
  const qrColour = [u.colours.lightShell, "#FEF8F4"].includes(
    stripTags(card.businessCardColour)
  )
    ? u.colours.charcoal
    : card.businessCardColour || u.colours.charcoal;

  const [profileOpen, setProfileOpen] = React.useState(false);
  const [isFlipped, setIsFlipped] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  function handleClick() {
    if (!props.isStatic) setProfileOpen(!profileOpen);
    else setIsFlipped(!isFlipped);
  }

  function onImageError() {
    setImageError(true);
  }

  return (
    <>
      {profileOpen && (
        <MemberProfileModal {...props} onClose={() => setProfileOpen(false)} />
      )}
      <ReactCardFlip
        isFlipped={isFlipped}
        cardStyles={{
          front: { transformStyle: "flat" },
          back: { transformStyle: "flat" },
        }}
      >
        <S.BusinessCard
          colour={card.businessCardColour}
          isStatic={props.isStatic}
          onClick={() => handleClick()}
          data-insights-object-id={card.objectID}
          data-insights-position={card.__position}
          data-insights-query-id={card.__queryID}
          data-cy="member-card"
        >
          <S.BackgroundOverlay colour={card.businessCardColour} />
          <S.HeaderContent>
            <S.Row>
              <I.AllBrightVector colour={textColour} width={20} height={20} />
              <T.CS weight="600" letterSpacing=".2rem" colour={textColour}>
                BUSINESS CARD
              </T.CS>
            </S.Row>
            <S.FullName
              colour={textColour}
              dangerouslySetInnerHTML={{ __html: sanitise(card.displayName) }}
            />
            {card.location && (
              <S.Row>
                <I.Location colour={textColour} />
                <T.CS
                  weight="500"
                  colour={textColour}
                  dangerouslySetInnerHTML={{ __html: sanitise(card.location) }}
                />
              </S.Row>
            )}
          </S.HeaderContent>
          {card.imageSrc && card.displayPhoto && !imageError && (
            <S.ImageWrap>
              <Image
                onError={onImageError}
                src={card.imageSrc}
                layout="fill"
                objectFit="cover"
              />
            </S.ImageWrap>
          )}
          {props.isStatic && (
            <>
              <S.TapMeWrap colour={textColour}>
                <T.CS weight="600" colour={card.businessCardColour}>
                  tap me
                </T.CS>
              </S.TapMeWrap>
              <S.TapMeTriangle colour={textColour} />
            </>
          )}
          <S.Info>
            {role.length > 3 ? (
              <S.InfoSection>
                <T.CS weight="600" colour={textColour}>
                  Role
                </T.CS>
                <T.CS
                  weight="500"
                  colour={textColour}
                  dangerouslySetInnerHTML={{ __html: sanitise(role) }}
                />
              </S.InfoSection>
            ) : (
              userInterests && (
                <S.InfoSection>
                  <T.CS weight="600" colour={textColour}>
                    Interests
                  </T.CS>
                  <T.CS
                    weight="500"
                    colour={textColour}
                    dangerouslySetInnerHTML={{
                      __html: sanitise(userInterests),
                    }}
                  />
                </S.InfoSection>
              )
            )}
            {goals ? (
              <S.InfoSection>
                <T.CS weight="600" colour={textColour}>
                  Seeking
                </T.CS>
                <T.CS
                  weight="500"
                  colour={textColour}
                  dangerouslySetInnerHTML={{ __html: sanitise(goals) }}
                />
              </S.InfoSection>
            ) : (
              <U.Space height={60} />
            )}
          </S.Info>
        </S.BusinessCard>
        <S.QRCode colour={qrColour} onClick={() => handleClick()}>
          <RxQrCode
            value={`${config.ALLBRIGHT_URL}/member/${card.id}?qrcode=true`}
            bgColor={qrColour}
          />
        </S.QRCode>
      </ReactCardFlip>
    </>
  );
};

const S = () => {};

S.BusinessCard = styled.div<StyleProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 260px;
  width: 300px;
  border-radius: 5px;
  padding: 15px 20px 0 15px;
  transition: transform 0.2s;
  justify-content: space-between;
  cursor: pointer;
  background: ${(p) => p.colour || u.colours.lightShell};

  @media ${u.device.mobile} {
    height: 260px;
    width: 300px;
  }

  ${(p) =>
    p.isStatic
      ? `
    box-shadow: 0 0 15px ${u.colours.charcoalFaded};
    overflow: hidden;
  `
      : `
    border: 1px solid ${u.colours.shellOverlay};
    &:hover {
      transform: scale(1.02);
    }
  `}
`;

S.QRCode = styled.div<StyleProps>`
  background: ${(p) => p.colour || u.colours.lightShell};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 260px;
  width: 300px;
  border-radius: 5px;
  cursor: pointer;
  box-shadow: 0 0 15px ${u.colours.charcoalFaded};
`;

S.BackgroundOverlay = styled.div<StyleProps>`
  left: 0;
  bottom: 0;
  height: 60px;
  width: 100%;
  position: absolute;
  border-radius: 0 0 5px 5px;
  filter: brightness(0.95);
  background: ${(p) => p.colour || u.colours.lightShell};
`;

S.HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 70%;
`;

S.ImageWrap = styled.div`
  height: 70px;
  width: 70px;
  position: absolute;
  top: 20px;
  right: 20px;
  border-radius: 50%;
  overflow: hidden;
`;

S.TapMeWrap = styled.div<StyleProps>`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 50px;
  width: 100px;
  padding: 0 0 8px 0;
  position: absolute;
  top: -7.5px;
  right: -32.5px;
  z-index: 2;
  transform: rotate(45deg);
`;

S.TapMeTriangle = styled.div<StyleProps>`
  position: absolute;
  top: -7.5px;
  right: -32.5px;
  width: 0;
  height: 0;
  border-left: 50px solid transparent;
  border-right: 50px solid transparent;
  border-bottom: 50px solid ${(p) => p.colour};
  transform: rotate(45deg);
`;

S.FullName = styled(T.H4)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

S.Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

S.Info = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 2;
  bottom: 0;
`;

S.InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 60px;
`;

export default BusinessCard;
